import {
  IFinancingBoard,
  IFinancingRights,
  IRoundBoardResponse,
  IRoundGovernanceTerms,
  IRoundRightsResponse,
} from '../../../data-models/round-governance.data-model';

/**
 * Realistic governance payloads for unit tests and local UI iteration: a
 * term-sheet-only extraction (4 of 18 terms known) with our fund holding an
 * unnamed board seat and information rights.
 */

export const fixtureGovernanceTerms = (
  overrides: Partial<IRoundGovernanceTerms> = {}
): IRoundGovernanceTerms => ({
  boardSize: null,
  dragAlong: null,
  cumulativeDividends: null,
  antiDilutionRights: null,
  redemptionRights: null,
  payToPlay: null,
  rofrAndCosaleAgreement: null,
  standardProRataFormulation: null,
  employeeVestingProtocol: null,
  investorCounselFeeCap: 50_000,
  issuerPaysInvestorCounselFees: true,
  requiredClosingPayments: false,
  registrationRightsForPreferred: null,
  majorInvestorThresholdDollars: null,
  majorInvestorThresholdShares: null,
  proRataRightsForMajorInvestors: null,
  proRataRightsForAll: null,
  informationRightsForMajorInvestors: null,
  informationRightsForAll: null,
  qsbsRepMade: 'Yes',
  qsbsCovenant: 'Yes',
  protectiveSectionsReference: null,
  reporting: null,
  ...overrides,
});

export const fixtureRights = (
  roundId: number,
  financing: Partial<IFinancingRights> = {}
): IRoundRightsResponse => ({
  roundId,
  financings: [
    {
      source: 'DEAL_DOC',
      dealDocId: 9001,
      financingName: 'Series C Preferred Financing',
      stage: 'Series C',
      date: '2021-11-11',
      terms: fixtureGovernanceTerms(),
      investors: [
        {
          fundName: 'Test Fund',
          fundId: 'fund-test-1',
          isMajorInvestor: false,
          hasInformationRights: true,
          hasProRataRights: null,
          majorInvestorQualificationBasis: null,
          informationRightsBasis: 'Term sheet §4(b)',
          rightsStatus: 'ACTIVE',
          informationRightsStatus: 'ACTIVE',
          rightsEffectiveDate: '2021-11-11',
          rightsTerminationDate: null,
          rightsVerifiedDate: null,
          rightsVerifiedBy: null,
          hasSideLetterOverride: null,
          sideLetterTerms: null,
          sideLetterDate: null,
          statusChangeDate: null,
          statusChangeReason: null,
        },
        {
          fundName: 'Tiger Global Management',
          fundId: null,
          isMajorInvestor: true,
          hasInformationRights: true,
          hasProRataRights: true,
          majorInvestorQualificationBasis: 'Lead investor',
          informationRightsBasis: null,
          rightsStatus: 'ACTIVE',
          informationRightsStatus: 'ACTIVE',
          rightsEffectiveDate: '2021-11-11',
          rightsTerminationDate: null,
          rightsVerifiedDate: null,
          rightsVerifiedBy: null,
          hasSideLetterOverride: null,
          sideLetterTerms: null,
          sideLetterDate: null,
          statusChangeDate: null,
          statusChangeReason: null,
        },
      ],
      ...financing,
    },
  ],
});

export const fixtureBoardFinancing = (overrides: Partial<IFinancingBoard> = {}): IFinancingBoard => ({
  source: 'DEAL_DOC',
  dealDocId: 9001,
  financingName: 'Series C Preferred Financing',
  stage: 'Series C',
  date: '2021-11-11',
  totalBoardSize: 5,
  boardSizeIsDerived: false,
  derivedSeatCount: 2,
  vacantSeatCount: 0,
  observerPresent: true,
  seats: [
    { seatStatus: 'HELD_UNNAMED', memberName: null, firm: 'Test Fund', fundId: 'fund-test-1' },
    { seatStatus: 'FILLED', memberName: 'Jason Miller', firm: 'Tiger Global Management', fundId: null },
  ],
  observers: [{ name: 'Dana Whitfield', firm: 'Greycroft', fundId: null }],
  seatCountNote: null,
  ...overrides,
});

export const fixtureBoard = (
  roundId: number,
  overrides: Partial<IFinancingBoard> = {}
): IRoundBoardResponse => ({
  roundId,
  financings: [fixtureBoardFinancing(overrides)],
});
